import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { PageHero, Section, SectionHead, Loader } from "@/components/Blocks";
import { AssetIcon } from "@/components/AssetIcon";
import { EventCard } from "@/components/EventCard";
import { RiskNotice } from "@/components/layout/PublicLayout";

export function Courses() {
  const { data, loading } = useFetch("/courses");
  const program = (data || []).find((c) => c.slug === "mission-control");
  const modules = (data || []).filter((c) => c.slug !== "mission-control");
  return (
    <>
      <PageHero eyebrow="Courses" title="Five markets. One mission." sub="Every module teaches risk first, then structure, then the Mission Control method for that market." />
      <Section>
        {loading ? <Loader /> : (
          <div className="grid gap-4 md:grid-cols-12">
            {program && (
              <Link to="/courses/mission-control" data-testid="course-card-mission-control"
                className="group relative overflow-hidden rounded-lg bg-[#0B1B33] p-8 text-white transition-transform duration-200 hover:-translate-y-1 md:col-span-12 lg:col-span-8 lg:row-span-2">
                <img src="/brand/hero_art.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
                <div className="relative">
                  <div className="eyebrow">Flagship · All five markets</div>
                  <h2 className="mt-3 font-display text-5xl font-black uppercase leading-none">{program.full_name}</h2>
                  <p className="mt-4 max-w-lg text-white/75">{program.short}</p>
                  <p className="mt-2 text-sm text-white/55">{program.duration}</p>
                  <span className="btn-gold mt-8">View the full program <ArrowRight /></span>
                </div>
              </Link>
            )}
            {modules.map((c, i) => (
              <Link key={c.slug} to={`/courses/${c.slug}`} data-testid={`course-card-${c.slug}`}
                className={`rise group panel p-6 transition-transform transition-colors duration-200 hover:-translate-y-1 hover:border-[#E0A21B] ${i < 2 ? "md:col-span-6 lg:col-span-4" : "md:col-span-4"}`}
                style={{ animationDelay: `${i * 60}ms` }}>
                <AssetIcon name={c.icon} size={36} className="text-[#E0A21B]" />
                <h3 className="mt-3 font-display text-2xl font-bold uppercase">{c.full_name}</h3>
                <p className="mt-2 text-sm text-[#475569]">{c.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold group-hover:text-[#E0A21B]">Details <ArrowRight /></span>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

export function CourseDetail() {
  const { slug } = useParams();
  const { data: c, loading, error } = useFetch(`/courses/${slug}`);
  if (loading) return <Loader />;
  if (error) return <Section><p data-testid="course-error">{error}</p></Section>;
  return (
    <>
      <PageHero eyebrow={c.slug === "mission-control" ? "Flagship program" : "Market module"} title={c.full_name} sub={c.short}>
        <div className="mt-6 flex flex-wrap gap-3 text-sm text-white/70"><span className="rounded-full border border-white/20 px-3 py-1">{c.duration}</span></div>
      </PageHero>
      <Section tone="white">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHead eyebrow="Mission objectives" title="What you will learn" />
            <ul className="space-y-3">
              {c.learn.map((l) => <li key={l} className="flex gap-3"><CheckCircle size={22} weight="fill" className="shrink-0 text-[#1E8E5A]" /> {l}</li>)}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="panel p-6">
              <div className="eyebrow">Flight plan</div>
              <ol className="mt-4 space-y-3">
                {c.weekly_plan.map((w, i) => (
                  <li key={w} className="flex gap-3 text-sm"><span className="font-mono-num text-[#E0A21B]">{String(i + 1).padStart(2, "0")}</span>{w}</li>
                ))}
              </ol>
            </div>
            <RiskNotice className="mt-4" />
          </div>
        </div>
      </Section>
      <Section>
        <SectionHead eyebrow="Book a seat" title="Upcoming classes" sub="Every module is taught inside the full-day Mission Control class in each city." />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {c.events.map((e, i) => <EventCard key={e.id} e={e} i={i} />)}
        </div>
      </Section>
    </>
  );
}
